import { Dialog, Portal } from '@halo/ui-react';
import { StateEnum, useAppStore, useGroupMembers, type GroupMember } from '@halo/chat-sdk-react';
import { ButtonGray, Empty, SearchInput, UserItem } from '@halo/ui-chat-react';
import { Box, Flex } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useActionsStore } from '@/store/actions';
import { usePortalContainer } from '@/scripts/hooks';

export interface TransferToDialogProps {
  sessionId?: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  handleTransferToClick: (member: GroupMember) => void;
  onClose?: () => void;
}

export function TransferToDialog({
  sessionId,
  isOpen,
  setIsOpen,
  handleTransferToClick,
}: TransferToDialogProps) {
  const [searchValue, setSearchValue] = useState('');
  const { t } = useTranslation();
  const { user: currentUser } = useAppStore();
  const { fetchProfileUser, setTransferToIsOpen } = useActionsStore();
  const { members, run } = useGroupMembers(sessionId);

  const { portalContainerRef } = usePortalContainer();

  const filteredMembers = useMemo(() => {
    return members?.filter((member) => {
      const isMe = member.user_id === currentUser?.user_id;
      if (isMe || member.state === StateEnum.DELETE_ACCOUNT) {
        return false;
      }
      return member.name?.toLowerCase().includes(searchValue?.toLowerCase());
    });
  }, [currentUser?.user_id, members, searchValue]);

  const onMemberAvatarClick = useCallback(
    async (member: GroupMember) => {
      fetchProfileUser(member.user_id);
    },
    [fetchProfileUser]
  );

  useEffect(() => {
    if (!isOpen) {
      setSearchValue('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      return run();
    }
  }, [isOpen, run]);

  return (
    <Dialog.Root
      placement="center"
      open={isOpen}
      onOpenChange={(details) => setIsOpen(details.open)}
      lazyMount
      unmountOnExit
      restoreFocus={false}
      trapFocus={false}
      closeOnInteractOutside={false}
    >
      <Portal container={portalContainerRef}>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content pt="2.5" px="5" pb="5" w="388px" maxW="100%">
            <Dialog.Header
              px="0"
              py="2.5"
              fontSize="md"
              fontWeight="semibold"
              justifyContent="space-between"
            >
              <Box>{t('action.transferTo')}</Box>
            </Dialog.Header>
            <Dialog.Body p="0" py="2">
              <SearchInput value={searchValue} onChange={(value) => setSearchValue(value)} />
              <Box pt="2">
                <Box
                  className="scrollHover"
                  borderRadius="lg"
                  p="1"
                  maxH="500px"
                  overflowY="auto"
                >
                  {filteredMembers?.map((member, index) => (
                    <UserItem
                      key={member.user_id}
                      {...member}
                      rippleEffect={true}
                      hoverable={true}
                      cursor="pointer"
                      onAvatarClick={() => onMemberAvatarClick?.(member)}
                      onClick={() => handleTransferToClick?.(member)}
                      borderColor={
                        index === filteredMembers.length - 1 ? 'transparent' : 'border.subtle'
                      }
                    />
                  ))}
                  {filteredMembers?.length === 0 && <Empty />}
                </Box>
              </Box>
              <Flex justifyContent="flex-end" pt="8">
                <ButtonGray onClick={() => setTransferToIsOpen(false)}>
                  {t('buttons.cancel')}
                </ButtonGray>
              </Flex>
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
